import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Star, StarOff, Eye, EyeOff, Trash2, RotateCcw, ChevronLeft, ChevronRight, MoreVertical, AlertTriangle, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, runBulk } from "@/components/common/BulkActionBar";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";

const TYPES = [
  { value: "resource", label: "Resources" },
  { value: "event", label: "Events" },
  { value: "business", label: "Businesses" },
  { value: "capital", label: "Capital" },
  { value: "personal", label: "People" },
];

// Public canonical URL prefix per content type (only types with a live public page).
const LIVE_PREFIX = { personal: "/p/", business: "/b/", capital: "/capital/" };

const SORTS = [
  { value: "recent", label: "Recently updated" },
  { value: "title_asc", label: "Title A–Z" },
  { value: "title_desc", label: "Title Z–A" },
  { value: "status", label: "Status" },
];

export default function AdminContent() {
  const [type, setType] = useState("resource");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null); // { item, mode: 'remove' | 'purge' }
  const [bulkConfirm, setBulkConfirm] = useState(null); // 'remove' | 'purge'

  const ids = useMemo(() => (data?.items || []).map((i) => i.id), [data]);
  const sel = useRowSelection(ids);

  const load = useCallback(async () => {
    setData(null);
    try {
      const params = { type, sort, page, page_size: 20 };
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/content", { params });
      setData(data);
    } catch { setData({ items: [], total: 0 }); }
  }, [type, status, sort, q, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); sel.clear(); /* eslint-disable-next-line */ }, [type, status, sort, q]);

  const act = async (item, action) => {
    try {
      await api.post(`/admin/content/${type}/${item.id}/action`, { action });
      toast.success({
        publish: "Published", unpublish: "Unpublished", remove: "Moved to deleted",
        restore: "Restored", purge: "Permanently deleted", feature: "Featured", unfeature: "Unfeatured",
      }[action] || "Done");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setConfirm(null); }
  };

  const bulkAct = async (action, verb) => {
    await runBulk(sel.selected, (id) => api.post(`/admin/content/${type}/${id}/action`, { action }), verb);
    sel.clear(); setBulkConfirm(null); load();
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;
  const canFeature = type === "resource" || type === "event";
  const colCount = canFeature ? 5 : 4;

  return (
    <div data-testid="admin-content">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-[640] text-foreground">Content</h2>
        <AdminRefreshButton testId="admin-refresh-content" onRefresh={load} />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full sm:w-44" data-testid="admin-content-type"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative flex-1">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" data-testid="admin-content-search" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full sm:w-44" data-testid="admin-content-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-40" data-testid="admin-content-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="mt-4 border-0 p-0">
        {!data ? <div className="p-6"><Loading /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} data-testid="admin-content-select-all" /></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  {canFeature && <TableHead>Featured</TableHead>}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((it) => {
                  const isDeleted = it.status === "deleted";
                  const isArchived = it.status === "archived";
                  const liveUrl = LIVE_PREFIX[data.type] && it.published && it.slug ? `${LIVE_PREFIX[data.type]}${it.slug}` : null;
                  return (
                    <TableRow key={it.id} data-state={sel.isSelected(it.id) ? "selected" : undefined} data-testid={`admin-content-${it.id}`}>
                      <TableCell><Checkbox checked={sel.isSelected(it.id)} onCheckedChange={() => sel.toggle(it.id)} data-testid={`admin-content-select-${it.id}`} /></TableCell>
                      <TableCell>
                        <div className="font-[540] text-foreground">{it.title}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{it.slug || `id: ${it.id}`}</div>
                      </TableCell>
                      <TableCell>
                        {isDeleted ? <StatusBadge tone="danger">Deleted</StatusBadge>
                          : isArchived ? <StatusBadge tone="warning">Archived</StatusBadge>
                          : it.published ? <StatusBadge tone="success">Live</StatusBadge>
                          : <StatusBadge tone="neutral">Draft</StatusBadge>}
                      </TableCell>
                      {canFeature && <TableCell>{it.featured ? <StatusBadge tone="warning"><Star className="mr-1 h-3 w-3" /> Featured</StatusBadge> : <span className="text-muted-foreground">—</span>}</TableCell>}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {liveUrl && (
                            <a href={liveUrl} target="_blank" rel="noreferrer">
                              <Button size="sm" variant="ghost" data-testid={`admin-content-viewlive-${it.id}`} title="View live profile"><ExternalLink className="h-4 w-4" /></Button>
                            </a>
                          )}
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`admin-content-actions-${it.id}`} title="Actions"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {!isDeleted && (it.published
                              ? <DropdownMenuItem onClick={() => act(it, "unpublish")} data-testid={`admin-content-unpublish-${it.id}`}><EyeOff className="mr-2 h-4 w-4" /> Unpublish</DropdownMenuItem>
                              : <DropdownMenuItem onClick={() => act(it, "publish")} data-testid={`admin-content-publish-${it.id}`}><Eye className="mr-2 h-4 w-4" /> Publish</DropdownMenuItem>)}
                            {canFeature && !isDeleted && (it.featured
                              ? <DropdownMenuItem onClick={() => act(it, "unfeature")} data-testid={`admin-content-unfeature-${it.id}`}><StarOff className="mr-2 h-4 w-4" /> Unfeature</DropdownMenuItem>
                              : <DropdownMenuItem onClick={() => act(it, "feature")} data-testid={`admin-content-feature-${it.id}`}><Star className="mr-2 h-4 w-4" /> Feature</DropdownMenuItem>)}
                            {(isDeleted || isArchived) && (
                              <DropdownMenuItem onClick={() => act(it, "restore")} data-testid={`admin-content-restore-${it.id}`}><RotateCcw className="mr-2 h-4 w-4" /> Restore</DropdownMenuItem>
                            )}
                            {!isDeleted && (
                              <DropdownMenuItem onClick={() => setConfirm({ item: it, mode: "remove" })} data-testid={`admin-content-remove-${it.id}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirm({ item: it, mode: "purge" })} data-testid={`admin-content-purge-${it.id}`}><AlertTriangle className="mr-2 h-4 w-4" /> Permanently delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data.items.length === 0 && <TableRow><TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">No content matches.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{data?.total ?? 0} items</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-content-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
          <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-content-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <BulkActionBar count={sel.count} onClear={sel.clear}>
        <Button size="sm" variant="secondary" onClick={() => bulkAct("publish", "Published")} data-testid="bulk-publish"><Eye className="mr-1.5 h-4 w-4" /> Publish</Button>
        <Button size="sm" variant="secondary" onClick={() => bulkAct("unpublish", "Unpublished")} data-testid="bulk-unpublish"><EyeOff className="mr-1.5 h-4 w-4" /> Unpublish</Button>
        <Button size="sm" variant="secondary" onClick={() => bulkAct("restore", "Restored")} data-testid="bulk-restore"><RotateCcw className="mr-1.5 h-4 w-4" /> Restore</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkConfirm("remove")} data-testid="bulk-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkConfirm("purge")} data-testid="bulk-purge"><AlertTriangle className="mr-1.5 h-4 w-4" /> Permanently delete</Button>
      </BulkActionBar>

      {/* Single-row confirm */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          {confirm?.mode === "purge" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Permanently delete "{confirm?.item?.title}"?</AlertDialogTitle>
                <AlertDialogDescription>This <strong>cannot be undone</strong>. The item is erased from the database for good. To keep it recoverable, use "Delete" instead.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => act(confirm.item, "purge")} data-testid="admin-content-purge-confirm">Permanently delete</AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{confirm?.item?.title}"?</AlertDialogTitle>
                <AlertDialogDescription>This unpublishes and soft-deletes the item. It stays recoverable — you can restore it from the Deleted filter.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => act(confirm.item, "remove")} data-testid="admin-content-remove-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk confirm */}
      <AlertDialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <AlertDialogContent>
          {bulkConfirm === "purge" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Permanently delete {sel.count} item{sel.count === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription>This <strong>cannot be undone</strong>. The selected items are erased from the database for good.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => bulkAct("purge", "Permanently deleted")} data-testid="bulk-purge-confirm">Permanently delete</AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {sel.count} item{sel.count === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription>This unpublishes and soft-deletes the selected items. They stay recoverable from the Deleted filter.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => bulkAct("remove", "Deleted")} data-testid="bulk-delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
